// ─────────────────────────────────────────────────────────────────────────────
// THE PREVENT-PLANT FACTOR, AND WHETHER WE ACTUALLY KNOW IT
//
// BUG-010. `getPPFactor(crop)` returns RMA's BASE prevent-plant coverage factor
// and takes no argument that could tell it whether the insured bought the factor
// UP. A grower who elects option `PF` carries a factor 5 (or 10) percentage
// points higher, and every surface built on the bare table therefore printed a
// prevent-plant guarantee below the one the policy actually carries — with
// arithmetic that looks perfectly valid.
//
// The wheat quote sheet was repaired in place on 2026-09-02 (src/wheatPpPrior.js,
// read out of RMA's own A01220_GuaranteeAdjustment member). Nothing else was, and
// the comment above PP_FACTORS in App.jsx said so: "The table below is therefore
// the BASE factor per crop and does NOT carry the buy-up tier."
//
// ── WHAT IS ACTUALLY LIVE, MEASURED 2026-09-04 ──────────────────────────────
//
// The report this came in as said corn and soybeans carry the same understatement.
// Measured against the book they do NOT, and the reason matters: PF is elected on
// FALL crops only. CY2026 `crop_coverage.insurance_options`:
//
//     WHEAT     184 of 446 rows carry PF        CORN      0 of 939
//     BARLEY      7 of  14                      SOYBEANS  0 of 659
//     TRITICALE   1 of   2                      DRY BEANS 0 of  67, POTATOES 0 of 24
//
// Back-solved independently out of RMA's OWN stated per-unit guarantee — P11 #120
// `acre_guarantee_qty` ÷ (aph × coverage level), over every CY2026 prevent-plant
// unit — the applied factor is 0.5502 corn (n=79), 0.6010 soybeans (17), 0.4500
// potatoes (23), 0.5003 dry beans (2), 0.6007 grain sorghum (1). Every one of
// those matches the base table. The single exception is 0.6491 on the one wheat
// PP unit in the book (ANCHOR FARMS, ERIE) — 60% + 5%, PF elected.
//
// So the corn/soy half of the report is LATENT, not live, and this module is
// written to hold the whole class rather than to patch the seven rows that hurt.
//
// LIVE HARM, on the Fall Crops → Prevent Plant tab today: seven pp_eligibility
// rows across four farms whose matching coverage elects PF and whose PP Guarantee
// per acre is therefore printed five points light.
//
//     AUSTIC FARM PARTNERS  Seneca   WHEAT  APH 89 @ 75%   $230.69 -> $249.91/ac  x 884.5 ac
//     AUSTIC FARM PARTNERS  Tompkins WHEAT  APH 83 @ 75%   $215.14 -> $233.06     x 110.3
//     AUSTIC FARM PARTNERS  Schuyler WHEAT  APH 89 @ 75%   $230.69 -> $249.91     x  71.5
//     RICE, CRAIG           Livingston WHEAT APH 82 @ 50%  $141.70 -> $153.50     x 250.7
//     STRAPP FARM LLC       Ontario  WHEAT  APH 74 @ 80%   $204.60 -> $221.64     x  85.7
//     PALLADINO AND CARLEY  Onondaga BARLEY (two APH lines, no max acres on file)
//
// About $24,800 of prevent-plant coverage that a grower has bought and the page
// does not show.
//
// ── THE LADDER, AND WHY EACH RUNG IS WHERE IT IS ────────────────────────────
//
//   1. `aip_stated`   RMA's own per-unit guarantee quantity (P11 #120) divided by
//                     (approved yield x coverage level). This ALREADY carries every
//                     adjustment — the buy-up, and the 1%/day late-planting slide
//                     this app cannot model at all. It is a reading, not a model,
//                     so it outranks everything below it. NAU populates it; R&H
//                     does not send #120 at all, which is why the rungs below are
//                     load-bearing rather than defensive.
//   2. `adm_wheat`    RMA's published CY2026 A01220 table, per (state, county,
//                     practice), through src/wheatPpPrior.js. WHEAT only, one crop
//                     year only, and a county outside the quoter's footprint
//                     returns nothing rather than borrowing a neighbour's cell.
//   3. `buyup_floor`  PF is elected and no source above states the tier. RMA sells
//                     the buy-up at 5 OR 10 points and the option code `PF` does
//                     not say which, so this returns a RANGE and no point value.
//                     Inventing +5 here because +5 is what wheat happened to be
//                     would be an inference that fits the data — the failure this
//                     repo's rule zero opens with.
//   4. `base`         The election is CAPTURED and PF is absent. This is the AIP
//                     stating no buy-up, and it is exact.
//   5. `not_captured` Nothing in the file ever carried the election (a hand-typed
//                     Agri Sompo / ProAg book, or a PP eligibility with no insured
//                     coverage to read an election from). NOT the same answer as
//                     `base`, and it must not read as one.
//
// ⚠ AN ABSENT OPTION AND AN UNCAPTURED ONE ARE DIFFERENT ANSWERS. That is the
// same three-state rule cbsOptState() already holds for the Coverage Book Scan,
// and it is why `optionsCaptured` is a REQUIRED input rather than something this
// module guesses from the string being empty. A blank options field on a
// sync-format policy is the AIP saying "nothing elected"; a blank one on a
// hand-typed book is us never having asked.
//
// ── THE BASE IS A FLOOR, WHICH IS WHY UNKNOWN IS STILL USEFUL ───────────────
//
// RMA sells a prevent-plant buy-UP and no buy-DOWN, so whatever the election, the
// factor is at least the base. `low` is therefore always a real lower bound and a
// consumer can honestly print "at least $X/ac" rather than a dash. Blanking 72 of
// the book's 779 CY2026 eligibility rows would have been the letter of "unknown
// stays unknown" and the wrong reading of it: a number with its uncertainty named
// beats no number, and it never understates without saying so.
// ─────────────────────────────────────────────────────────────────────────────

import { wheatPpFactorPrior, WHEAT_PP_PRIOR_YEAR } from "./wheatPpPrior.js";

// RMA base prevent-plant coverage factors. NOT the elected factor — see above.
export const PP_FACTORS = {
  "CORN": 0.55, "SOYBEANS": 0.60, "WHEAT": 0.60, "WINTER WHEAT": 0.60,
  "BARLEY": 0.60, "OATS": 0.60, "RYE": 0.60, "TRITICALE": 0.60,
  "GRAIN SORGHUM": 0.60, "DRY BEANS": 0.50, "PROCESSING BEANS": 0.40,
  "POTATOES": 0.45, "ONIONS": 0.35, "SILAGE SORGHUM": 0.50,
};

export function ppBaseFactor(crop) {
  if (!crop) return 0.55;
  const up = String(crop).toUpperCase();
  // Silage corn (GSS type) uses the corn factor.
  if (up === "SILAGE" || up === "CORN SILAGE") return PP_FACTORS["CORN"] || 0.55;
  return PP_FACTORS[up] || 0.55;
}

// RMA sells the prevent-plant buy-up at FIVE or TEN percentage points. The option
// code carried in the M13 file is a single `PF` either way, so a policy electing it
// is somewhere in this band unless a stated guarantee says exactly where.
export const PP_BUYUP_MIN = 0.05;
export const PP_BUYUP_MAX = 0.10;

// Split the stored options string the same way parseOptionCodes does — three
// stored formats ("QL,TA,YA", "EU, TA, YA, YC, QL", "EU01,QL,YA,YC"), so split on
// anything non-alphabetic and fold EU01 to EU. Kept here rather than imported
// because App.jsx exports nothing; check-pp-factor asserts the two agree.
export function ppOptionCodes(raw) {
  return String(raw || "").toUpperCase().split(/[^A-Z]+/).filter(Boolean)
    .map((c) => (c.startsWith("EU") ? "EU" : c));
}

const finite = (v) => v != null && Number.isFinite(Number(v));

// ⚠ THE ADM TABLE IS KEYED ON FIPS AND `policies.state` IS POSTAL. Measured on the
// live book: NY 580 policies, PA 180, MI 29, OH 24, MD 11, IN 3 — every one a
// two-letter code, while WHEAT_PP_PRIOR_TABLE keys look like "36|SENECA|003".
// Passing one where the other is expected does not throw: the lookup matches
// nothing, the rung is skipped and the factor quietly falls to the base — the
// exact understatement this module exists to remove, arriving through the fix.
// Same trap as the PA|HARFORD county lookup and the sco_eco_county postal key
// already recorded in CLAUDE.md, so BOTH forms are accepted here.
//
// An unmappable state returns NULL and the wheat rung is skipped. App.jsx's own
// stFips() defaults an unknown state to "36", which is right for a New York book
// and would put a Michigan policy on a New York factor; a borrowed neighbour is
// not an answer.
const POSTAL_FIPS = { NY: "36", PA: "42", MD: "24", OH: "39", IN: "18", MI: "26" };
export function ppStateFips(st) {
  const v = String(st == null ? "" : st).trim().toUpperCase();
  if (/^[0-9]{1,2}$/.test(v)) return v.padStart(2, "0");
  return POSTAL_FIPS[v] || null;
}

/**
 * The one place that decides what prevent-plant factor a coverage carries.
 *
 * Every field is optional EXCEPT `crop`; the ladder simply falls further when a
 * caller cannot supply the better inputs. `optionsCaptured` is the one that must
 * be passed deliberately — see the three-state note above.
 *
 * @returns {{
 *   factor: number|null,   // the exact factor, or null when only a range is known
 *   low: number,           // a genuine lower bound, always a number
 *   high: number,          // the highest the factor could be
 *   exact: boolean,        // low === high === factor
 *   basis: string,         // aip_stated | adm_wheat | buyup_floor | base | not_captured
 *   pf: boolean|null,      // PF elected? null = we cannot say
 *   note: string           // why, in words, for the screen
 * }}
 */
export function resolvePpFactor(input) {
  const {
    crop, cropYear = null, state = null, county = null, practiceCode = null,
    options = null, optionsCaptured = false,
    acreGuaranteeQty = null, aphYield = null, coverageLevel = null,
  } = input || {};

  const base = ppBaseFactor(crop);
  const codes = ppOptionCodes(options);
  // `pf` is THREE-valued on purpose. false means the AIP told us no buy-up;
  // null means nobody ever told us. Collapsing them is the whole defect.
  const pf = optionsCaptured ? codes.includes("PF") : null;

  // ── 1. the AIP's own stated guarantee ─────────────────────────────────────
  // acre_guarantee_qty is bushels/acre of guarantee AFTER every adjustment, so
  // dividing by (approved yield x coverage level) recovers the factor exactly.
  // Bounded rather than trusted blindly: a stale or mis-keyed row lands outside
  // any real PP factor, and a wrong reading here would outrank the truth below.
  if (finite(acreGuaranteeQty) && finite(aphYield) && finite(coverageLevel)
      && Number(aphYield) > 0 && Number(coverageLevel) > 0) {
    const f = Number(acreGuaranteeQty) / (Number(aphYield) * Number(coverageLevel));
    if (f >= 0.20 && f <= 1.05) {
      const r = Math.round(f * 10000) / 10000;
      return { factor: r, low: r, high: r, exact: true, basis: "aip_stated", pf,
        note: "read from the AIP's own per-acre guarantee quantity" };
    }
  }

  // ── 2. RMA's published table, for the one crop-year it covers ─────────────
  if (String(crop || "").toUpperCase().startsWith("WHEAT")
      && Number(cropYear) === Number(WHEAT_PP_PRIOR_YEAR)
      && state != null && county != null) {
    const w = wheatPpFactorPrior(ppStateFips(state), county, practiceCode, options);
    if (w && w.base != null) {
      // The ADM states BOTH tiers for the cell, so an uncaptured election still
      // narrows to base..pfFactor rather than base..base+10pts.
      if (pf === true && w.pfFactor != null) {
        return { factor: w.pfFactor, low: w.pfFactor, high: w.pfFactor, exact: true,
          basis: "adm_wheat", pf: true, note: "RMA's published buy-up factor for this county" };
      }
      if (pf === false) {
        return { factor: w.base, low: w.base, high: w.base, exact: true,
          basis: "adm_wheat", pf: false, note: "RMA's published base factor; the AIP reports no buy-up" };
      }
      const hi = w.pfFactor != null ? w.pfFactor : w.base;
      return { factor: null, low: w.base, high: hi, exact: false, basis: "not_captured", pf: null,
        note: "the buy-up election was never captured for this policy" };
    }
  }

  // ── 3. PF elected, tier not stated anywhere ──────────────────────────────
  if (pf === true) {
    return { factor: null, low: base + PP_BUYUP_MIN, high: base + PP_BUYUP_MAX, exact: false,
      basis: "buyup_floor", pf: true,
      note: "the buy-up is elected; RMA sells it at 5 or 10 points and the file does not say which" };
  }

  // ── 4. the AIP states no buy-up ──────────────────────────────────────────
  if (pf === false) {
    return { factor: base, low: base, high: base, exact: true, basis: "base", pf: false,
      note: "the AIP reports no prevent-plant buy-up on this coverage" };
  }

  // ── 5. nobody ever told us ───────────────────────────────────────────────
  return { factor: null, low: base, high: base + PP_BUYUP_MAX, exact: false,
    basis: "not_captured", pf: null,
    note: "the buy-up election was never captured for this policy" };
}

// What to print in a "PP Factor" cell. Exact -> "65%". Otherwise the band, so a
// reader can never mistake a floor for a figure.
export function ppFactorLabel(r) {
  if (!r) return "—";
  if (r.exact && r.factor != null) return `${(r.factor * 100).toFixed(0)}%`;
  // ⚠ A REFUSED OR UNRESOLVED FACTOR HAS NO NUMBER, AND (null * 100).toFixed(0) IS
  // "0". So a withheld factor printed a confident 0% beside a withheld guarantee —
  // seen on a real card 2026-09-15, where three CY2027 wheat rows read "0%" next to
  // an em-dash. A factor with no bounds is an absence, not a zero.
  if (r.low == null || r.high == null) return "—";
  const lo = (r.low * 100).toFixed(0), hi = (r.high * 100).toFixed(0);
  return lo === hi ? `${lo}%` : `${lo}–${hi}%`;
}
